// CANONICAL COPY (GH #7115). src/constants/frameworks/nis2.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

// NIS2 Directive (EU) 2022/2555 — framework mapping to TCF controls.
// Article 21 defines 10 minimum cybersecurity risk-management measures.
// Article 20 covers management body accountability.
// Article 23 covers incident reporting obligations.
//
// Each NIS2 article measure maps to one or more TCF controls.
// Multiple mappings with the same externalId are intentional — the last entry
// per externalId is used for the scfLookup (cross-reference display), while
// all entries contribute to controlToFrameworks and externalRefs.

const FRAMEWORK_NIS2 = {
  id: 'fw-nis2',
  type: 'law',
  name: 'NIS2 Directive',
  shortName: 'NIS2',
  abbrev: 'NIS2',
  description:
    'EU Network and Information Security Directive 2 — mandatory cybersecurity requirements for essential and important entities operating in critical sectors across the European Union.',
  color: '#3b82f6',
  version: '2022/2555',
  jurisdiction: 'EU',
  sectionKeyDepth: 1,
  // One section per measure, mirroring the article headings the mappings below
  // are grouped under. Article 21(2) is split into its ten lettered measures
  // because each is a distinct obligation rather than a sub-point.
  sections: [
    { key: 'NIS2-ART20-1', name: 'Art. 20 — Management body accountability' },
    {
      key: 'NIS2-ART21-1',
      name: 'Art. 21(2)(a) — Risk analysis and information security policies',
    },
    { key: 'NIS2-ART21-2', name: 'Art. 21(2)(b) — Incident handling' },
    {
      key: 'NIS2-ART21-3',
      name: 'Art. 21(2)(c) — Business continuity, backup and disaster recovery',
    },
    { key: 'NIS2-ART21-4', name: 'Art. 21(2)(d) — Supply chain security' },
    {
      key: 'NIS2-ART21-5',
      name: 'Art. 21(2)(e) — Security in acquisition, development and maintenance',
    },
    { key: 'NIS2-ART21-6', name: 'Art. 21(2)(f) — Policies to assess effectiveness' },
    { key: 'NIS2-ART21-7', name: 'Art. 21(2)(g) — Basic cyber hygiene and training' },
    { key: 'NIS2-ART21-8', name: 'Art. 21(2)(h) — Cryptography and encryption' },
    {
      key: 'NIS2-ART21-9',
      name: 'Art. 21(2)(i) — HR security, access control and asset management',
    },
    { key: 'NIS2-ART21-10', name: 'Art. 21(2)(j) — MFA and secured communications' },
    { key: 'NIS2-ART23-1', name: 'Art. 23 — Reporting obligations' },
  ],
  mappings: [
    // ── Article 20 — Management body accountability ───────────────────────
    { externalId: 'NIS2-ART20-1', scfId: 'SCF-GOV-004', ref: 'Art. 20' },
    { externalId: 'NIS2-ART20-1', scfId: 'SCF-PPL-003', ref: 'Art. 20' }, // management-body training duty (Art. 20(2))
    { primary: true, externalId: 'NIS2-ART20-1', scfId: 'SCF-GOV-012', ref: 'Art. 20' }, // primary

    // ── Article 21(2)(a) — Risk analysis and IS policies ──────────────────
    { externalId: 'NIS2-ART21-1', scfId: 'SCF-GOV-001', ref: 'Art. 21(2)(a)' },
    { externalId: 'NIS2-ART21-1', scfId: 'SCF-RSK-002', ref: 'Art. 21(2)(a)' },
    { primary: true, externalId: 'NIS2-ART21-1', scfId: 'SCF-RSK-001', ref: 'Art. 21(2)(a)' }, // primary

    // ── Article 21(2)(b) — Incident handling ──────────────────────────────
    { externalId: 'NIS2-ART21-2', scfId: 'SCF-INC-004', ref: 'Art. 21(2)(b)' },
    { externalId: 'NIS2-ART21-2', scfId: 'SCF-INC-003', ref: 'Art. 21(2)(b)' },
    { externalId: 'NIS2-ART21-2', scfId: 'SCF-INC-002', ref: 'Art. 21(2)(b)' },
    { primary: true, externalId: 'NIS2-ART21-2', scfId: 'SCF-INC-001', ref: 'Art. 21(2)(b)' }, // primary

    // ── Article 21(2)(c) — Business continuity, backup, DR ───────────────
    { externalId: 'NIS2-ART21-3', scfId: 'SCF-BCM-003', ref: 'Art. 21(2)(c)' },
    { externalId: 'NIS2-ART21-3', scfId: 'SCF-BCM-002', ref: 'Art. 21(2)(c)' },
    { primary: true, externalId: 'NIS2-ART21-3', scfId: 'SCF-BCM-001', ref: 'Art. 21(2)(c)' }, // primary

    // ── Article 21(2)(d) — Supply chain security ──────────────────────────
    { externalId: 'NIS2-ART21-4', scfId: 'SCF-VDR-004', ref: 'Art. 21(2)(d)' },
    { externalId: 'NIS2-ART21-4', scfId: 'SCF-VDR-002', ref: 'Art. 21(2)(d)' },
    { externalId: 'NIS2-ART21-4', scfId: 'SCF-VDR-001', ref: 'Art. 21(2)(d)' },
    { primary: true, externalId: 'NIS2-ART21-4', scfId: 'SCF-VDR-003', ref: 'Art. 21(2)(d)' }, // primary

    // ── Article 21(2)(e) — Security in acquisition, development, maintenance
    { externalId: 'NIS2-ART21-5', scfId: 'SCF-OPS-004', ref: 'Art. 21(2)(e)' }, // vulnerability handling/disclosure
    { externalId: 'NIS2-ART21-5', scfId: 'SCF-OPS-005', ref: 'Art. 21(2)(e)' },
    { externalId: 'NIS2-ART21-5', scfId: 'SCF-DEV-008', ref: 'Art. 21(2)(e)' },
    { externalId: 'NIS2-ART21-5', scfId: 'SCF-DEV-005', ref: 'Art. 21(2)(e)' },
    { externalId: 'NIS2-ART21-5', scfId: 'SCF-DEV-002', ref: 'Art. 21(2)(e)' },
    { primary: true, externalId: 'NIS2-ART21-5', scfId: 'SCF-DEV-001', ref: 'Art. 21(2)(e)' }, // primary

    // ── Article 21(2)(f) — Policies to assess effectiveness ───────────────
    { externalId: 'NIS2-ART21-6', scfId: 'SCF-MSS-006', ref: 'Art. 21(2)(f)' },
    { externalId: 'NIS2-ART21-6', scfId: 'SCF-MSS-005', ref: 'Art. 21(2)(f)' },
    { externalId: 'NIS2-ART21-6', scfId: 'SCF-AUD-002', ref: 'Art. 21(2)(f)' },
    { primary: true, externalId: 'NIS2-ART21-6', scfId: 'SCF-AUD-001', ref: 'Art. 21(2)(f)' }, // primary

    // ── Article 21(2)(g) — Basic cyber hygiene and training ───────────────
    { externalId: 'NIS2-ART21-7', scfId: 'SCF-OPS-004', ref: 'Art. 21(2)(g)' },
    { externalId: 'NIS2-ART21-7', scfId: 'SCF-OPS-003', ref: 'Art. 21(2)(g)' },
    { primary: true, externalId: 'NIS2-ART21-7', scfId: 'SCF-PPL-003', ref: 'Art. 21(2)(g)' }, // primary

    // ── Article 21(2)(h) — Cryptography and encryption ────────────────────
    { externalId: 'NIS2-ART21-8', scfId: 'SCF-DAT-001', ref: 'Art. 21(2)(h)' },
    { primary: true, externalId: 'NIS2-ART21-8', scfId: 'SCF-NET-005', ref: 'Art. 21(2)(h)' }, // primary

    // ── Article 21(2)(i) — HR security, access control, asset management ──
    { externalId: 'NIS2-ART21-9', scfId: 'SCF-AST-001', ref: 'Art. 21(2)(i)' },
    { externalId: 'NIS2-ART21-9', scfId: 'SCF-IAM-004', ref: 'Art. 21(2)(i)' },
    { externalId: 'NIS2-ART21-9', scfId: 'SCF-IAM-002', ref: 'Art. 21(2)(i)' },
    { externalId: 'NIS2-ART21-9', scfId: 'SCF-PPL-002', ref: 'Art. 21(2)(i)' },
    { externalId: 'NIS2-ART21-9', scfId: 'SCF-PPL-001', ref: 'Art. 21(2)(i)' },
    { primary: true, externalId: 'NIS2-ART21-9', scfId: 'SCF-IAM-001', ref: 'Art. 21(2)(i)' }, // primary

    // ── Article 21(2)(j) — MFA and secured communications ────────────────
    { externalId: 'NIS2-ART21-10', scfId: 'SCF-NET-002', ref: 'Art. 21(2)(j)' },
    { externalId: 'NIS2-ART21-10', scfId: 'SCF-NET-001', ref: 'Art. 21(2)(j)' },
    { primary: true, externalId: 'NIS2-ART21-10', scfId: 'SCF-IAM-008', ref: 'Art. 21(2)(j)' }, // primary

    // ── Article 23 — Reporting obligations ────────────────────────────────
    { externalId: 'NIS2-ART23-1', scfId: 'SCF-GOV-005', ref: 'Art. 23' },
    { primary: true, externalId: 'NIS2-ART23-1', scfId: 'SCF-INC-006', ref: 'Art. 23' }, // primary
  ],
};

module.exports = {
  FRAMEWORK_NIS2,
};
